import { 
  createIcons, 
  ArrowLeft, 
  Plus, 
  Layers, 
  Boxes, 
  Edit3, 
  Trash2, 
  Eye, 
  FolderKanban, 
  Building2, 
  Calendar, 
  User, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ExternalLink,
  GitBranch,
  FileText
} from "lucide";
import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import type { Project, ProjectUnitArea, ProjectStatus, UnitAreaStatus } from "../../domain/project/project-domain.js";

export class AppProjectDetailPage extends HTMLElement {
  private projectId: string | null = null;
  private project: Project | null = null;
  private unitAreas: ProjectUnitArea[] = [];
  private equipments: any[] = [];
  private workOrders: any[] = [];
  private client: any = null;
  private activeTab: 'UNITS' | 'EQUIPMENTS' | 'WORK_ORDERS' = 'UNITS';
  private isLoading = true;
  private errorMessage: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  static get observedAttributes() {
    return ['project-id', 'id'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if ((name === 'project-id' || name === 'id') && oldValue !== newValue && newValue) {
      this.projectId = newValue;
      this.loadData();
    }
  }

  connectedCallback() {
    if (!this.projectId) {
      this.projectId = this.getAttribute('project-id') || this.getAttribute('id');
    }
    // Also parse from hash if needed (e.g. #/management/projects/PRJ-123)
    if (!this.projectId && window.location.hash) {
      const parts = window.location.hash.split('/');
      const last = parts[parts.length - 1];
      if (last && last !== 'projects') {
        this.projectId = last;
      }
    }

    this.loadData();
    this.unsubscribe = contextStore.subscribe(() => {
      this.loadData();
    });
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData() {
    if (!this.projectId) return;

    this.isLoading = true;
    this.errorMessage = null;
    this.render();

    try {
      const activeClientId = contextStore.get().activeClientId;
      const dummyContext = { activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;

      const [prj, uas, eqs, wos] = await Promise.all([
        window.app.platform.projectService.getProject(this.projectId, sessionRequestContext()),
        window.app.platform.projectService.listUnitAreas(this.projectId, undefined, sessionRequestContext()),
        window.app.platform.equipmentService.listEquipments(dummyContext).catch(() => []),
        window.app.platform.adminService.listWorkOrders(dummyContext).catch(() => [])
      ]);

      if (!prj) {
        this.errorMessage = "Projeto não encontrado.";
        this.isLoading = false;
        this.render();
        return;
      }

      this.project = prj;
      this.unitAreas = uas || [];
      // Filter equipments linked to this project
      this.equipments = (eqs || []).filter((e: any) => e.projectId === this.projectId);
      // Filter work orders linked to this project
      this.workOrders = (wos || []).filter((w: any) => w.projectId === this.projectId);

      // Load client details
      try {
        this.client = await window.app.platform.adminService.getClient(dummyContext, prj.clientId);
      } catch (e) {
        this.client = null;
      }

      this.isLoading = false;
      this.render();
    } catch (e: any) {
      this.isLoading = false;
      this.errorMessage = e.safeMessage || e.message || "Erro ao carregar detalhes do projeto.";
      this.render();
    }
  }

  private escapeHtml(str?: string | null): string {
    if (!str) return '';
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private getStatusBadge(status: ProjectStatus): string {
    switch (status) {
      case 'ACTIVE':
        return `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 3px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #4ade80;"></span> ATIVO</span>`;
      case 'PLANNING':
        return `<span style="background: rgba(59, 130, 246, 0.15); color: var(--ia-info-fg, #60a5fa); border: 1px solid rgba(59, 130, 246, 0.3); padding: 3px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #60a5fa;"></span> PLANEJAMENTO</span>`;
      case 'COMPLETED':
        return `<span style="background: rgba(168, 85, 247, 0.15); color: var(--ia-info-fg, #c084fc); border: 1px solid rgba(168, 85, 247, 0.3); padding: 3px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #c084fc;"></span> CONCLUÍDO</span>`;
      case 'CANCELLED':
        return `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 3px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-danger-solid);"></span> CANCELADO</span>`;
      default:
        return `<span style="background: rgba(140, 140, 140, 0.15); color: var(--ia-fg-soft, #b5b5b5); padding: 3px 8px; border-radius: 4px; font-size: 0.75rem;">${status}</span>`;
    }
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando detalhes do projeto...</span>
        </div>
      `;
      return;
    }

    if (this.errorMessage || !this.project) {
      this.innerHTML = `
        <div style="padding: 28px; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: var(--radius-sm); margin: 24px; color: var(--status-danger-text);">
          <h3 style="margin: 0 0 8px 0; font-size: 1rem; font-weight: 600;">Projeto Não Encontrado</h3>
          <p style="margin: 0 0 16px 0; font-size: 0.875rem; ;">${this.errorMessage || 'Projeto inexistente.'}</p>
          <button id="btn-back-projects" class="btn btn-secondary btn-sm">Voltar aos Projetos</button>
        </div>
      `;
      this.querySelector('#btn-back-projects')?.addEventListener('click', () => router.navigate('/management/projects'));
      return;
    }

    const prj = this.project;
    const clientName = this.client ? (this.client.name || this.client.legalName) : prj.clientId;

    this.innerHTML = `
      <style>
        .prjd-container { padding: 24px; max-width: 1400px; margin: 0 auto; }
        .prjd-breadcrumb { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; color: var(--color-text-disabled); margin-bottom: 16px; }
        .prjd-breadcrumb a { color: var(--ia-info-fg, #60a5fa); text-decoration: none; font-weight: 500; }
        .prjd-breadcrumb a:hover { text-decoration: underline; }
        
        .prjd-header-card { background: var(--ia-bg-panel, #18181b); border: 1px solid var(--ia-line, #27272a); border-radius: 8px; padding: 20px; margin-bottom: 24px; }
        .prjd-top-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
        .prjd-title { margin: 0 0 6px 0; font-size: 1.4rem; font-weight: 700; color: var(--color-text-primary); display: flex; align-items: center; gap: 10px; }
        .prjd-code-badge { font-family: monospace; font-size: 0.85rem; font-weight: 700; color: var(--ia-info-fg, #60a5fa); background: rgba(59, 130, 246, 0.12); border: 1px solid rgba(59, 130, 246, 0.25); padding: 3px 8px; border-radius: 4px; }
        
        .prjd-info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; border-top: 1px solid #27272a; padding-top: 16px; }
        .prjd-info-item label { display: block; font-size: 0.72rem; color: var(--color-text-primary); text-transform: uppercase; font-weight: 600; margin-bottom: 3px; }
        .prjd-info-item span { font-size: 0.875rem; color: var(--color-text-primary); font-weight: 500; }
        
        .prjd-tabs-nav { display: flex; gap: 4px; border-bottom: 2px solid #27272a; margin-bottom: 20px; }
        .prjd-tab-btn { background: transparent; border: none; padding: 12px 18px; font-size: 0.9rem; font-weight: 600; color: var(--color-text-disabled); cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; display: flex; align-items: center; gap: 8px; transition: all 0.2s; }
        .prjd-tab-btn:hover { color: var(--color-text-primary); }
        .prjd-tab-btn.active { color: var(--ia-info-fg, #60a5fa); border-bottom-color: var(--color-ink); background: rgba(59, 130, 246, 0.05); }
        
        .prjd-table-wrapper { background: var(--ia-bg-panel, #18181b); border: 1px solid var(--ia-line, #27272a); border-radius: 8px; overflow: hidden; }
        .prjd-table { width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem; }
        .prjd-table th { background: var(--ia-bg-panel, #202024); color: var(--color-text-disabled); font-weight: 600; padding: 12px 16px; border-bottom: 1px solid var(--ia-line, #27272a); }
        .prjd-table td { padding: 14px 16px; border-bottom: 1px solid #27272a; color: var(--color-text-primary); vertical-align: middle; }
        .prjd-table tr:hover td { background: rgba(255, 255, 255, 0.02); }
        
        .btn-primary-prj { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, var(--color-text-primary)); border: none; padding: 8px 16px; border-radius: 6px; font-size: 0.875rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; }
        .btn-primary-prj:hover { background: var(--color-primary); }
        .btn-back-link { background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 7px 12px; border-radius: 6px; font-size: 0.8rem; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
        .btn-back-link:hover { background: var(--ia-bg-raised, #3f3f46); color: var(--color-text-primary); }
        .btn-action-sm { background: var(--ia-bg-panel, #27272a); border: 1px solid var(--color-input-border); color: var(--color-text-primary); padding: 5px 8px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; display: inline-flex; align-items: center; gap: 4px; }
        .btn-action-sm:hover { background: var(--ia-bg-raised, #3f3f46); color: var(--color-text-primary); }
        .btn-action-sm.danger:hover { background: rgba(239, 68, 68, 0.2); border-color: var(--status-danger-solid); color: var(--status-danger-border); }
      </style>

      <div class="prjd-container">
        <!-- BREADCRUMB -->
        <div class="prjd-breadcrumb">
          <a href="#/management/projects"><i data-lucide="folder-kanban" style="width: 14px; height: 14px; display: inline; vertical-align: middle;"></i> Projetos</a>
          <span>&rsaquo;</span>
          <span style="color: var(--ia-info-fg, #60a5fa);"><i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(clientName)}</span>
          <span>&rsaquo;</span>
          <span style="color: var(--color-text-primary); font-weight: 600;">${this.escapeHtml(prj.code)} - ${this.escapeHtml(prj.name)}</span>
        </div>

        <!-- HEADER CARD -->
        <div class="prjd-header-card">
          <div class="prjd-top-row">
            <div>
              <h2 class="prjd-title">
                <span class="prjd-code-badge">${this.escapeHtml(prj.code)}</span>
                ${this.escapeHtml(prj.name)}
                ${this.getStatusBadge(prj.status)}
              </h2>
              <p style="margin: 0; color: var(--color-text-disabled); font-size: 0.875rem;">
                ${this.escapeHtml(prj.description || 'Sem descrição informada.')}
              </p>
            </div>
            <div style="display: flex; gap: 8px; flex-wrap: wrap;">
              <button class="btn-back-link" id="btn-back-to-projects">
                <i data-lucide="arrow-left" style="width: 15px; height: 15px;"></i> Voltar
              </button>
              <button class="btn-primary-prj" id="btn-create-os-for-project" style="background: rgba(56, 189, 248, 0.15); color: var(--color-text-muted); border: 1px solid rgba(56, 189, 248, 0.4);">
                <i data-lucide="file-text" style="width: 15px; height: 15px;"></i> + Emitir OS
              </button>
              <button class="btn-primary-prj" id="btn-add-unit">
                <i data-lucide="plus" style="width: 16px; height: 16px;"></i> Nova Unidade / área
              </button>
            </div>
          </div>

          <div class="prjd-info-grid">
            <div class="prjd-info-item">
              <label>Cliente Vinculado</label>
              <span style="color: var(--ia-info-fg, #93c5fa);"><i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(clientName)}</span>
            </div>
            <div class="prjd-info-item">
              <label>Responsável</label>
              <span><i data-lucide="user" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(prj.manager || '-')}</span>
            </div>
            <div class="prjd-info-item">
              <label>Data Início</label>
              <span><i data-lucide="calendar" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${prj.startDate ? new Date(prj.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</span>
            </div>
            <div class="prjd-info-item">
              <label>Término Previsto</label>
              <span><i data-lucide="calendar" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${prj.expectedEndDate ? new Date(prj.expectedEndDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</span>
            </div>
            <div class="prjd-info-item">
              <label>Unidades / áreas</label>
              <span style="color: var(--status-success-text); font-weight: 700;">${this.unitAreas.length} unidades</span>
            </div>
            <div class="prjd-info-item">
              <label>Equipamentos</label>
              <span style="color: var(--ia-info-fg, #60a5fa); font-weight: 700;">${this.equipments.length} ativos</span>
            </div>
            <div class="prjd-info-item">
              <label>Ordens de Serviço</label>
              <span style="color: var(--color-text-muted); font-weight: 700;">${this.workOrders.length} OS vinculadas</span>
            </div>
          </div>
        </div>

        <!-- TABS NAV -->
        <div class="prjd-tabs-nav">
          <button class="prjd-tab-btn ${this.activeTab === 'UNITS' ? 'active' : ''}" id="tab-btn-units">
            <i data-lucide="layers" style="width: 16px; height: 16px;"></i>
            1. Unidades / áreas do Projeto (${this.unitAreas.length})
          </button>
          <button class="prjd-tab-btn ${this.activeTab === 'EQUIPMENTS' ? 'active' : ''}" id="tab-btn-equipments">
            <i data-lucide="boxes" style="width: 16px; height: 16px;"></i>
            2. Equipamentos Vinculados (${this.equipments.length})
          </button>
          <button class="prjd-tab-btn ${this.activeTab === 'WORK_ORDERS' ? 'active' : ''}" id="tab-btn-work-orders">
            <i data-lucide="file-text" style="width: 16px; height: 16px;"></i>
            3. Ordens de Serviço Vinculadas (${this.workOrders.length})
          </button>
        </div>

        <!-- TAB CONTENT: UNITS -->
        ${this.activeTab === 'UNITS' ? `
          <div class="prjd-table-wrapper">
            <table class="prjd-table">
              <thead>
                <tr>
                  <th style="width: 130px;">Código Unidade</th>
                  <th>Nome da Unidade / área</th>
                  <th>Descrição</th>
                  <th style="text-align: center; width: 150px;">Ativos Vinculados</th>
                  <th style="width: 110px;">Status</th>
                  <th style="width: 160px; text-align: right;">Ações</th>
                </tr>
              </thead>
              <tbody>
                ${this.unitAreas.length === 0 ? `
                  <tr>
                    <td colspan="6" style="padding: 40px 16px; text-align: center; color: var(--color-text-primary);">
                      <i data-lucide="layers" style="width: 36px; height: 36px; ; margin-bottom: 8px;"></i>
                      <p style="margin: 0; font-size: 0.95rem;">Nenhuma unidade/área cadastrada neste projeto.</p>
                      <p style="margin: 4px 0 12px 0; font-size: 0.8rem; color: var(--color-text-primary);">Cadastre unidades (ex: Unidade de Processamento, Tancagem, Utilidades) para poder vincular equipamentos.</p>
                      <button class="btn-primary-prj" id="btn-empty-add-unit">
                        <i data-lucide="plus" style="width: 15px; height: 15px;"></i> Cadastrar Primeira Unidade
                      </button>
                    </td>
                  </tr>
                ` : this.unitAreas.map(u => {
                  const unitEqCount = this.equipments.filter((e: any) => e.unitAreaId === u.id || e.areaId === u.id).length;
                  return `
                    <tr>
                      <td>
                        <span style="font-family: monospace; font-weight: 700; color: var(--color-text-muted); background: rgba(56, 189, 248, 0.1); padding: 3px 6px; border-radius: 4px; border: 1px solid rgba(56, 189, 248, 0.2);">
                          ${this.escapeHtml(u.code)}
                        </span>
                      </td>
                      <td>
                        <strong style="color: var(--color-text-primary);">${this.escapeHtml(u.name)}</strong>
                      </td>
                      <td>
                        <span style="font-size: 0.8rem; color: var(--color-text-disabled);">${this.escapeHtml(u.description || '-')}</span>
                      </td>
                      <td style="text-align: center;">
                        <span style="background: var(--ia-bg-panel, #27272a); color: var(--color-border-default); font-weight: 700; padding: 2px 10px; border-radius: 12px; font-size: 0.8rem;">
                          ${unitEqCount} equipamentos
                        </span>
                      </td>
                      <td>
                        ${u.status === 'ACTIVE' 
                          ? `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 2px 6px; border-radius: 4px; font-size: 0.72rem; font-weight: 600;">ATIVA</span>`
                          : `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 2px 6px; border-radius: 4px; font-size: 0.72rem; font-weight: 600;">INATIVA</span>`
                        }
                      </td>
                      <td style="text-align: right; white-space: nowrap;">
                        <button class="btn-action-sm btn-edit-unit" data-id="${u.id}" title="Editar Unidade" style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; font-size: 0.75rem; margin-right: 6px; cursor: pointer;">
                          <i data-lucide="edit-3" style="width: 13px; height: 13px;"></i>
                          <span>Editar</span>
                        </button>
                        <button class="btn-action-sm danger btn-delete-unit" data-id="${u.id}" data-name="${this.escapeHtml(u.name)}" data-code="${this.escapeHtml(u.code)}" data-eq-count="${unitEqCount}" title="Excluir Unidade / área" style="background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.35); color: var(--status-danger-solid); font-weight: 600; display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer;">
                          <i data-lucide="trash-2" style="width: 13px; height: 13px;"></i>
                          <span>Excluir</span>
                        </button>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}

        <!-- TAB CONTENT: EQUIPMENTS -->
        ${this.activeTab === 'EQUIPMENTS' ? `
          <div style="margin-bottom: 12px; display: flex; justify-content: flex-end;">
            <button class="btn-primary-prj" id="btn-add-eq-to-project">
              <i data-lucide="plus" style="width: 15px; height: 15px;"></i> Novo Equipamento neste Projeto
            </button>
          </div>
          <div class="prjd-table-wrapper">
            <table class="prjd-table">
              <thead>
                <tr>
                  <th style="width: 130px;">TAG</th>
                  <th>Nome do Ativo</th>
                  <th>Unidade / área</th>
                  <th>Tipo</th>
                  <th>Fluido / Serviço</th>
                  <th style="width: 100px;">Status</th>
                  <th style="width: 130px; text-align: right;">Ações</th>
                </tr>
              </thead>
              <tbody>
                ${this.equipments.length === 0 ? `
                  <tr>
                    <td colspan="7" style="padding: 40px 16px; text-align: center; color: var(--color-text-primary);">
                      <i data-lucide="boxes" style="width: 36px; height: 36px; ; margin-bottom: 8px;"></i>
                      <p style="margin: 0; font-size: 0.95rem;">Nenhum equipamento cadastrado neste projeto.</p>
                      <p style="margin: 4px 0 12px 0; font-size: 0.8rem; color: var(--color-text-primary);">Cadastre equipamentos vinculados a este projeto e às suas unidades.</p>
                    </td>
                  </tr>
                ` : this.equipments.map((eq: any) => {
                  const unit = this.unitAreas.find(u => u.id === eq.unitAreaId || u.id === eq.areaId);
                  const unitName = unit ? `${unit.code} - ${unit.name}` : (eq.unitAreaName || eq.location || '-');
                  return `
                    <tr>
                      <td style="font-family: monospace; font-weight: 700; color: var(--ia-info-fg, #60a5fa);">${eq.tag}</td>
                      <td><strong style="color: var(--color-text-primary);">${this.escapeHtml(eq.name)}</strong></td>
                      <td><span style="font-size: 0.82rem; color: var(--color-text-muted);"><i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(unitName)}</span></td>
                      <td><span style="font-size: 0.8rem; color: var(--color-text-disabled);">${eq.equipmentType}</span></td>
                      <td><span style="font-size: 0.8rem; color: var(--color-text-disabled);">${this.escapeHtml(eq.service || eq.fluid || '-')}</span></td>
                      <td>
                        <span style="font-size: 0.75rem; font-weight: 600; color: ${eq.status === 'ACTIVE' ? '#4ade80' : 'var(--status-danger-solid)'};">
                          ${eq.status === 'ACTIVE' ? 'ATIVO' : eq.status}
                        </span>
                      </td>
                      <td style="text-align: right;">
                        <button class="btn-action-sm btn-view-eq-item" data-id="${eq.id}">
                          <i data-lucide="eye" style="width: 13px; height: 13px;"></i> Detalhes
                        </button>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}

        <!-- TAB CONTENT: WORK ORDERS -->
        ${this.activeTab === 'WORK_ORDERS' ? `
          <div class="prjd-table-wrapper">
            <div style="padding: 14px 16px; background: var(--ia-bg-panel, #202024); border-bottom: 1px solid var(--ia-line, #27272a); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;">
              <span style="font-size: 0.85rem; color: var(--color-text-disabled);">
                Ordens de Serviço deste Projeto: <strong style="color: var(--color-text-muted);">${this.workOrders.length}</strong>
              </span>
              <div style="display: flex; gap: 8px;">
                <button class="btn-action-sm btn-open-os-page" style="color: var(--color-text-muted); border-color: rgba(56, 189, 248, 0.4);">
                  <i data-lucide="external-link" style="width: 13px; height: 13px;"></i> Abrir na Aba de OS
                </button>
                <button class="btn-primary-prj" id="btn-tab-new-os" style="background: var(--color-ink); padding: 6px 12px; font-size: 0.8rem;">
                  <i data-lucide="plus" style="width: 14px; height: 14px;"></i> Nova Ordem de Serviço
                </button>
              </div>
            </div>
            <table class="prjd-table">
              <thead>
                <tr>
                  <th style="width: 130px;">Código OS</th>
                  <th>Área / Local</th>
                  <th>Descrição dos Serviços</th>
                  <th style="width: 120px;">Início</th>
                  <th style="width: 120px;">Término</th>
                  <th style="width: 120px;">Status</th>
                  <th style="width: 150px; text-align: right;">Ações</th>
                </tr>
              </thead>
              <tbody>
                ${this.workOrders.length === 0 ? `
                  <tr>
                    <td colspan="7" style="padding: 40px 16px; text-align: center; color: var(--color-text-primary);">
                      <i data-lucide="file-text" style="width: 36px; height: 36px; ; margin-bottom: 8px; color: var(--color-text-muted);"></i>
                      <p style="margin: 0; font-size: 0.95rem; color: var(--color-text-primary);">Nenhuma Ordem de Serviço cadastrada para este projeto.</p>
                      <p style="margin: 4px 0 14px 0; font-size: 0.82rem; color: var(--color-text-primary);">Crie ordens de serviço vinculadas a este projeto para coordenar as inspeções técnicas.</p>
                      <button class="btn-primary-prj" id="btn-empty-add-os" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8);">
                        <i data-lucide="plus" style="width: 15px; height: 15px;"></i> Emitir Primeira OS deste Projeto
                      </button>
                    </td>
                  </tr>
                ` : this.workOrders.map((wo: any) => {
                  return `
                    <tr>
                      <td style="font-family: monospace; font-weight: 700; color: var(--color-text-muted);">${this.escapeHtml(wo.code)}</td>
                      <td><strong style="color: var(--color-text-primary);">${this.escapeHtml(wo.area || '-')}</strong></td>
                      <td><span style="font-size: 0.82rem; color: var(--color-text-disabled);">${this.escapeHtml(wo.description || '-')}</span></td>
                      <td><span style="font-size: 0.8rem; color: var(--color-text-primary);"><i data-lucide="calendar" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${wo.startDate ? new Date(wo.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</span></td>
                      <td><span style="font-size: 0.8rem; color: var(--color-text-primary);"><i data-lucide="calendar" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${wo.endDate ? new Date(wo.endDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</span></td>
                      <td>
                        <span style="font-size: 0.75rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; background: ${wo.status === 'COMPLETED' ? 'rgba(34,197,94,0.15)' : wo.status === 'IN_PROGRESS' ? 'rgba(59,130,246,0.15)' : 'rgba(234,179,8,0.15)'}; color: ${wo.status === 'COMPLETED' ? '#4ade80' : wo.status === 'IN_PROGRESS' ? '#60a5fa' : 'var(--status-warning-solid)'};">
                          ${wo.status === 'COMPLETED' ? 'CONCLUÍDA' : wo.status === 'IN_PROGRESS' ? 'EM ANDAMENTO' : wo.status === 'OPEN' ? 'ABERTA' : wo.status}
                        </span>
                      </td>
                      <td style="text-align: right;">
                        <button class="btn-action-sm btn-view-wo-os-tab" data-id="${wo.id}" title="Gerenciar OS na aba de OS">
                          <i data-lucide="external-link" style="width: 13px; height: 13px;"></i> Gerenciar na Aba OS
                        </button>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}

        <div id="unit-modal-container"></div>
      </div>
    `;

    createIcons({
      icons: { ArrowLeft, Plus, Layers, Boxes, Edit3, Trash2, Eye, FolderKanban, Building2, Calendar, User, Clock, CheckCircle2, AlertCircle, ExternalLink, GitBranch, FileText },
      nameAttr: "data-lucide",
      root: this
    });

    this.bindEvents();
  }

  private bindEvents() {
    this.querySelector('#btn-back-to-projects')?.addEventListener('click', () => {
      router.navigate('/management/projects');
    });

    this.querySelector('#tab-btn-units')?.addEventListener('click', () => {
      this.activeTab = 'UNITS';
      this.render();
    });

    this.querySelector('#tab-btn-equipments')?.addEventListener('click', () => {
      this.activeTab = 'EQUIPMENTS';
      this.render();
    });

    this.querySelector('#tab-btn-work-orders')?.addEventListener('click', () => {
      this.activeTab = 'WORK_ORDERS';
      this.render();
    });

    const createOsHandler = () => {
      if (!this.project) return;
      router.navigate(`/management/os?projectId=${this.project.id}&clientId=${this.project.clientId}&newForProject=${this.project.id}`);
    };

    this.querySelector('#btn-create-os-for-project')?.addEventListener('click', createOsHandler);
    this.querySelector('#btn-tab-new-os')?.addEventListener('click', createOsHandler);
    this.querySelector('#btn-empty-add-os')?.addEventListener('click', createOsHandler);

    this.querySelectorAll('.btn-open-os-page').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!this.project) return;
        router.navigate(`/management/os?projectId=${this.project.id}&clientId=${this.project.clientId}`);
      });
    });

    this.querySelectorAll('.btn-view-wo-os-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        if (!this.project) return;
        router.navigate(`/management/os?projectId=${this.project.id}&clientId=${this.project.clientId}`);
      });
    });

    this.querySelector('#btn-add-unit')?.addEventListener('click', () => {
      this.openUnitModal();
    });

    this.querySelector('#btn-empty-add-unit')?.addEventListener('click', () => {
      this.openUnitModal();
    });

    this.querySelector('#btn-add-eq-to-project')?.addEventListener('click', () => {
      router.navigate('/equipment/new');
    });

    this.querySelectorAll('.btn-view-eq-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) router.navigate(`/equipment/${id}`);
      });
    });

    this.querySelectorAll('.btn-edit-unit').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) {
          const unit = this.unitAreas.find(u => u.id === id);
          if (unit) this.openUnitModal(unit);
        }
      });
    });

    this.querySelectorAll('.btn-delete-unit').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const name = (e.currentTarget as HTMLElement).getAttribute('data-name') || '';
        const code = (e.currentTarget as HTMLElement).getAttribute('data-code') || '';
        const eqCountStr = (e.currentTarget as HTMLElement).getAttribute('data-eq-count') || '0';
        const eqCount = parseInt(eqCountStr, 10) || 0;
        if (id) {
          this.openDeleteUnitConfirmModal(id, name, code, eqCount);
        }
      });
    });
  }

  private openDeleteUnitConfirmModal(id: string, name: string, code: string, eqCount: number): void {
    const modalContainer = this.querySelector('#unit-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 10px; width: 100%; max-width: 460px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.6); text-align: center; padding: 24px;">
          <div style="width: 48px; height: 48px; border-radius: 50%; background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px auto;">
            <i data-lucide="trash-2" style="width: 24px; height: 24px;"></i>
          </div>
          <h3 style="margin: 0 0 10px 0; font-size: 1.2rem; font-weight: 700; color: var(--color-text-primary);">Excluir Unidade / Área</h3>
          <p style="color: var(--color-text-disabled); font-size: 0.9rem; margin: 0 0 16px 0; line-height: 1.5;">
            Tem certeza que deseja excluir a unidade <strong style="color: var(--color-text-primary);">${this.escapeHtml(code ? `${code} - ${name}` : name)}</strong>? Esta operação é irreversível.
          </p>

          ${eqCount > 0 ? `
            <div style="background: rgba(234, 179, 8, 0.12); border: 1px solid var(--status-warning-border); color: var(--status-warning-border); padding: 10px 14px; border-radius: 6px; font-size: 0.8125rem; margin-bottom: 16px; text-align: left; display: flex; align-items: flex-start; gap: 8px;">
              <i data-lucide="alert-circle" style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px;"></i>
              <span><strong>Atenção:</strong> Existem <strong>${eqCount}</strong> equipamento(s) vinculados a esta unidade. Ao excluí-la, lembre-se de reatribuir esses equipamentos se necessário.</span>
            </div>
          ` : ''}

          <div id="unit-delete-error" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 8px 12px; border-radius: 6px; font-size: 0.82rem; margin-bottom: 14px; text-align: left;"></div>

          <div style="display: flex; justify-content: center; gap: 12px; margin-top: 8px;">
            <button type="button" id="btn-cancel-delete-unit-modal" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500;">Cancelar</button>
            <button type="button" id="btn-confirm-delete-unit-modal" style="background: var(--status-danger-solid); color: var(--color-text-primary); border: none; padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;">
              <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
              <span>Sim, Excluir</span>
            </button>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { Trash2, AlertCircle },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeDeleteModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-cancel-delete-unit-modal')?.addEventListener('click', closeDeleteModal);

    modalContainer.querySelector('#btn-confirm-delete-unit-modal')?.addEventListener('click', async () => {
      const errorDiv = modalContainer.querySelector('#unit-delete-error') as HTMLElement;
      if (errorDiv) errorDiv.style.display = 'none';

      try {
        await window.app.platform.projectService.deleteUnitArea(id, sessionRequestContext());
        closeDeleteModal();
        await this.loadData();
      } catch (err: any) {
        if (errorDiv) {
          errorDiv.textContent = err.safeMessage || err.message || "Erro ao excluir unidade.";
          errorDiv.style.display = 'block';
        }
      }
    });
  }

  private openUnitModal(unit?: ProjectUnitArea): void {
    const modalContainer = this.querySelector('#unit-modal-container') as HTMLElement;
    if (!modalContainer || !this.project) return;

    const isEdit = !!unit;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 580px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--ia-line, #27272a); display: flex; justify-content: space-between; align-items: center; background: var(--ia-bg-panel, #202024);">
            <h3 style="margin: 0; font-size: 1.15rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="layers" style="color: var(--color-text-muted); width: 20px; height: 20px;"></i>
              ${isEdit ? 'Editar Unidade / Área' : 'Nova Unidade / Área'}
            </h3>
            <button id="btn-close-unit-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>

          <form id="form-unit" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div id="unit-error-banner" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 10px 14px; border-radius: 6px; font-size: 0.85rem;"></div>

            <div style="background: rgba(59, 130, 246, 0.08); border: 1px solid rgba(59, 130, 246, 0.2); border-radius: 6px; padding: 10px 14px; font-size: 0.82rem; color: var(--color-border-default);">
              Vinculada ao Projeto: <strong>${this.escapeHtml(this.project.code)} - ${this.escapeHtml(this.project.name)}</strong>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 2fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px; font-weight: 600;">Código da Unidade *</label>
                <input type="text" name="code" required value="${this.escapeHtml(unit?.code || '')}" placeholder="Ex: UN-01, UT-TANQ" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px; font-weight: 600;">Nome da Unidade / Área *</label>
                <input type="text" name="name" required value="${this.escapeHtml(unit?.name || '')}" placeholder="Ex: Unidade de Processamento, Área de Tancagem" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Descrição / Detalhes</label>
              <textarea name="description" rows="2" placeholder="Localização física, limites de bateria, características..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;">${this.escapeHtml(unit?.description || '')}</textarea>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px; font-weight: 600;">Status</label>
              <select name="status" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                <option value="ACTIVE" ${unit?.status === 'ACTIVE' ? 'selected' : ''}>Ativa</option>
                <option value="INACTIVE" ${unit?.status === 'INACTIVE' ? 'selected' : ''}>Inativa</option>
              </select>
            </div>

            <div style="display: flex; ${isEdit ? 'justify-content: space-between;' : 'justify-content: flex-end;'} align-items: center; margin-top: 14px; border-top: 1px solid #27272a; padding-top: 16px;">
              ${isEdit ? `
                <button type="button" id="btn-modal-delete-unit" style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 8px 14px; border-radius: 6px; cursor: pointer; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;">
                  <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
                  <span>Excluir Unidade</span>
                </button>
              ` : ''}
              <div style="display: flex; gap: 8px;">
                <button type="button" id="btn-cancel-unit-modal" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 8px 16px; border-radius: 6px; cursor: pointer;">Cancelar</button>
                <button type="submit" class="btn-primary-prj">Salvar Unidade</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Layers, Trash2 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-close-unit-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-unit-modal')?.addEventListener('click', closeModal);

    if (isEdit && unit) {
      modalContainer.querySelector('#btn-modal-delete-unit')?.addEventListener('click', () => {
        const eqCount = this.equipments.filter(e => e.unitAreaId === unit.id).length;
        closeModal();
        this.openDeleteUnitConfirmModal(unit.id, unit.name, unit.code, eqCount);
      });
    }

    const form = modalContainer.querySelector('#form-unit') as HTMLFormElement;
    const errorBanner = modalContainer.querySelector('#unit-error-banner') as HTMLElement;

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorBanner) errorBanner.style.display = 'none';

      const formData = new FormData(form);
      const code = (formData.get('code') as string)?.trim();
      const name = (formData.get('name') as string)?.trim();
      const description = (formData.get('description') as string)?.trim();
      const status = (formData.get('status') as UnitAreaStatus) || 'ACTIVE';

      if (!code || !name) {
        if (errorBanner) {
          errorBanner.textContent = "Preencha o código e o nome da unidade/área.";
          errorBanner.style.display = 'block';
        }
        return;
      }

      try {
        if (isEdit && unit) {
          await window.app.platform.projectService.updateUnitArea(unit.id, {
            code,
            name,
            description,
            status
          }, sessionRequestContext());
        } else {
          await window.app.platform.projectService.createUnitArea({
            code,
            name,
            projectId: this.project!.id,
            description,
            status
          }, sessionRequestContext());
        }
        closeModal();
        await this.loadData();
      } catch (err: any) {
        if (errorBanner) {
          errorBanner.textContent = err.safeMessage || err.message || "Erro ao salvar unidade.";
          errorBanner.style.display = 'block';
        }
      }
    });
  }
}

if (!customElements.get('app-project-detail-page')) {
  customElements.define('app-project-detail-page', AppProjectDetailPage);
}



